<template>
  <div class="student-history-import">
    <header class="page-header">
      <div>
        <h2>历史学员导入</h2>
        <p class="subtitle">从 Excel 批量导入学员档案与可用推荐名额（仅供上线迁移；需管理员权限）</p>
      </div>
    </header>

    <section class="intro-card">
      <h3>列说明</h3>
      <ul>
        <li><strong>姓名</strong>、<strong>手机号</strong>（11 位 1 开头；若为历史格式可在<strong>末尾多写一个 <code>-</code> 或 <code>+</code></strong>，与纯 11 位彼此<strong>都算不同手机号</strong>）、<strong>身份证号</strong>（15 或 18 位）</li>
        <li><strong>可用名额个数</strong>：非负整数；名额为 0 时仅建立档案</li>
        <li><strong>有效期至</strong>：名额大于 0 时必填，格式 <code>yyyy-MM-dd</code>，截止当日 23:59:59</li>
      </ul>
      <p class="hint">若手机号（按单元格字面量，含末尾 <code>-</code> / <code>+</code>）或身份证号已存在，该行会<strong>跳过</strong>并在报告中记录。系统将自动查找或创建「推荐名额占位课程」与占位档案「历史被推荐学员」，用于关联每条推荐名额明细。</p>
    </section>

    <section class="action-panel">
      <button type="button" class="ghost-button" :disabled="busy" @click="downloadTemplate">
        下载模板
      </button>
      <label class="file-picker">
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          :disabled="busy"
          @change="onFileChange"
        />
        <span>{{ selectedFile ? selectedFile.name : '选择 Excel…' }}</span>
      </label>
      <button
        type="button"
        class="primary-button"
        :disabled="!selectedFile || busy"
        @click="submitImport"
      >
        {{ busy ? '导入中…' : '开始导入' }}
      </button>
    </section>

    <p v-if="message" :class="['global-message', messageKind]">{{ message }}</p>

    <section v-if="report" class="report-card">
      <h3>导入结果</h3>
      <div class="report-summary">
        <div><span class="label">数据行数</span><span class="value">{{ report.totalRows }}</span></div>
        <div><span class="label">新建学员</span><span class="value">{{ report.createdStudents }}</span></div>
        <div><span class="label">跳过（重复）</span><span class="value">{{ report.skippedDuplicates }}</span></div>
        <div><span class="label">名额记录条数</span><span class="value">{{ report.quotaRecordsCreated }}</span></div>
        <div><span class="label">含已过期名额的行</span><span class="value">{{ report.expiredQuotaRows }}</span></div>
      </div>

      <div v-if="report.errors && report.errors.length" class="subsection">
        <h4>错误</h4>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>行号</th>
                <th>姓名</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in report.errors" :key="'e-' + idx">
                <td>{{ item.row }}</td>
                <td>{{ item.name || '—' }}</td>
                <td>{{ item.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="report.warnings && report.warnings.length" class="subsection">
        <h4>警告 / 提示</h4>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>行号</th>
                <th>姓名</th>
                <th>说明</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in report.warnings" :key="'w-' + idx">
                <td>{{ item.row }}</td>
                <td>{{ item.name || '—' }}</td>
                <td>{{ item.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { adminDataMigrationService } from '@/services/adminDataMigrationService'

export default {
  name: 'StudentHistoryImport',
  data() {
    return {
      busy: false,
      selectedFile: null,
      report: null,
      message: '',
      messageKind: ''
    }
  },
  methods: {
    onFileChange(event) {
      const f = event.target.files && event.target.files[0]
      this.selectedFile = f || null
      this.report = null
      this.message = ''
    },
    async downloadTemplate() {
      this.message = ''
      this.busy = true
      try {
        await adminDataMigrationService.downloadStudentHistoryTemplate()
        this.message = '模板已下载'
        this.messageKind = 'ok'
      } catch (e) {
        console.error(e)
        let msg = e.message || '下载失败'
        const d = e.response && e.response.data
        if (d && typeof d === 'object' && !(d instanceof Blob) && d.error) {
          msg = d.error
        }
        this.message = msg
        this.messageKind = 'err'
      } finally {
        this.busy = false
      }
    },
    async submitImport() {
      if (!this.selectedFile) return
      this.busy = true
      this.message = ''
      this.report = null
      try {
        const res = await adminDataMigrationService.importStudentHistory(this.selectedFile)
        if (res.success) {
          this.report = res.data || null
          this.message = res.message || '导入完成'
          this.messageKind = 'ok'
        } else {
          this.message = res.error || res.message || '导入失败'
          this.messageKind = 'err'
        }
      } catch (e) {
        console.error(e)
        let msg = e.message || '导入失败'
        const d = e.response && e.response.data
        if (d && typeof d === 'object' && !(d instanceof Blob)) {
          if (d.error) {
            msg = d.error
          } else if (d.message && d.success === false) {
            msg = d.message
          }
        }
        this.message = msg
        this.messageKind = 'err'
      } finally {
        this.busy = false
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
        this.selectedFile = null
      }
    }
  }
}
</script>

<style scoped>
.student-history-import {
  padding: 1.5rem 2rem 3rem;
  max-width: 960px;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-header h2 {
  margin: 0 0 0.35rem;
  font-size: 1.5rem;
  color: #1a1a2e;
}

.subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.intro-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

.intro-card h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: #334155;
}

.intro-card ul {
  margin: 0 0 0.75rem;
  padding-left: 1.25rem;
  color: #475569;
  line-height: 1.6;
}

.hint {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.action-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.file-picker {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #fff;
  border: 1px dashed #94a3b8;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #475569;
}

.file-picker input {
  display: none;
}

.primary-button,
.ghost-button {
  padding: 0.55rem 1.15rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
}

.primary-button {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
}

.primary-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ghost-button {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.ghost-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.global-message {
  margin: 0 0 1rem;
  font-size: 0.9rem;
}

.global-message.ok {
  color: #059669;
}

.global-message.err {
  color: #dc2626;
}

.report-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
}

.report-card h3 {
  margin: 0 0 1rem;
  font-size: 1.1rem;
  color: #1e293b;
}

.report-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.report-summary > div {
  background: #f1f5f9;
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.report-summary .label {
  font-size: 0.75rem;
  color: #64748b;
}

.report-summary .value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
}

.subsection h4 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  color: #475569;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

th,
td {
  padding: 0.5rem 0.65rem;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
}

tbody tr:last-child td {
  border-bottom: none;
}
</style>
